import { Media, TranscriptWord, VideoProject } from "@/services/bedrock";

export const PLAYBACK_RATES = [1, 1.5, 2] as const;
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];
export const PLAYBACK_RATE_SHORTCUTS: Record<PlaybackRate, string> = {
  1: "Q",
  1.5: "W",
  2: "E",
};
export const TRANSCRIPT_SHORTCUT = "T";

export const QA_WATCH_COMPLETE_RATIO = 0.9;
export const QA_SKIP_SECONDS = 2;
export const QA_SKIP_CUE_STACK_MS = 800;

export function stackSkipCueSeconds(
  current: { seconds: number; at: number } | null,
  actualDelta: number,
  now: number,
  requestedDelta: number,
  stackMs = QA_SKIP_CUE_STACK_MS
): number | null {
  const recent = Boolean(current && now - current.at < stackMs);
  const sameDirection = recent && Math.sign(current!.seconds) === Math.sign(requestedDelta);
  if (sameDirection) return current!.seconds + actualDelta;
  if (actualDelta === 0) return null;
  return actualDelta;
}

export type QaStudioSource = "EDITED" | "ORIGINAL";

export interface QaStudioClip {
  key: string;
  source: QaStudioSource;
  media: Media;
}

function sortStudioMedia(media?: Media[]): Media[] {
  return [...(media || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function qaStudioClips(editedMedia?: Media[], originalMedia?: Media[]): QaStudioClip[] {
  return [
    ...sortStudioMedia(editedMedia).map(media => ({
      key: `edited-${media.id}`,
      source: "EDITED" as const,
      media,
    })),
    ...sortStudioMedia(originalMedia).map(media => ({
      key: `original-${media.id}`,
      source: "ORIGINAL" as const,
      media,
    })),
  ];
}

export function isVideoMedia(media?: Media): boolean {
  return Boolean(media && media.category !== "IMAGE" && (media.category === "VIDEO" || media.uri));
}

export function clipForQaIssue(clips: QaStudioClip[]): QaStudioClip | undefined {
  return clips.find(clip => clip.source === "EDITED") ?? clips[0];
}

export function transcriptFromMedia(media?: Media): string | undefined {
  const texts = media?.transcripts?.map(transcript => transcript.content).filter(Boolean) as string[] | undefined;
  if (!texts?.length) return undefined;
  return [...texts].sort((a, b) => b.length - a.length)[0];
}

export interface TimedLine {
  startMs: number;
  endMs: number;
  text: string;
  words?: TranscriptWord[];
}

export type DiffKind = "equal" | "add" | "remove";

export interface DiffPart {
  text: string;
  kind: DiffKind;
  startMs?: number;
  endMs?: number;
}

export interface DiffToken {
  display: string;
  key: string;
  startMs?: number;
  endMs?: number;
}

export interface TranscriptComparisonRow {
  startMs?: number;
  endMs?: number;
  onScreenText?: string;
  spokenText?: string;
  onScreenParts: DiffPart[];
  spokenParts: DiffPart[];
}

const SRT_TIMECODE = /(\d{1,2}):(\d{2}):(\d{2})[,.](\d{1,3})\s*-->\s*(\d{1,2}):(\d{2}):(\d{2})[,.](\d{1,3})/;
const SPOKEN_PHRASE_GAP_MS = 700;
const SPOKEN_PHRASE_MAX_WORDS = 12;
const SPOKEN_PHRASE_ORPHAN_WORDS = 2;

function endsSpokenPhrase(word: string): boolean {
  return /[.!?…]$/.test(word);
}

function remainingWordsUntilSpokenBreak(words: TranscriptWord[], startIndex: number, previousEndMs: number): number {
  let remaining = 0;
  let previousEnd = previousEndMs;
  for (let index = startIndex; index < words.length; index++) {
    const word = words[index];
    if (word.startMs - previousEnd > SPOKEN_PHRASE_GAP_MS) break;
    remaining += 1;
    previousEnd = word.endMs;
    if (endsSpokenPhrase(word.word)) break;
  }
  return remaining;
}

function srtTimecodeToMs(match: RegExpMatchArray, offset: number): number {
  const hours = Number(match[1 + offset]);
  const minutes = Number(match[2 + offset]);
  const seconds = Number(match[3 + offset]);
  const millis = Number(
    String(match[4 + offset])
      .padEnd(3, "0")
      .slice(0, 3)
  );
  return ((hours * 60 + minutes) * 60 + seconds) * 1000 + millis;
}

function parseSrtCues(content?: string): TimedLine[] {
  if (!content?.trim()) return [];
  const blocks = content
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split(/\n{2,}/);
  const cues: TimedLine[] = [];
  for (const block of blocks) {
    const lines = block
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean);
    const timeIndex = lines.findIndex(line => SRT_TIMECODE.test(line));
    if (timeIndex < 0) continue;
    const match = lines[timeIndex].match(SRT_TIMECODE);
    if (!match) continue;
    const text = lines
      .slice(timeIndex + 1)
      .join("\n")
      .trim();
    if (!text) continue;
    cues.push({
      startMs: srtTimecodeToMs(match, 0),
      endMs: srtTimecodeToMs(match, 4),
      text,
    });
  }
  return cues;
}

export function srtCuesFromMedia(media?: Media): TimedLine[] {
  const cues = media?.caption?.cues;
  if (cues?.length) {
    return cues.map(cue => ({
      startMs: cue.startMs,
      endMs: cue.endMs,
      text: cue.text,
    }));
  }
  return parseSrtCues(media?.caption?.content);
}

function spokenWordsFromMedia(media?: Media): TranscriptWord[] {
  return (
    [...(media?.transcripts || [])]
      .map(transcript => transcript.timeline || [])
      .sort((a, b) => b.length - a.length)[0] || []
  );
}

export function spokenTimelineLines(words?: TranscriptWord[]): TimedLine[] {
  if (!words?.length) return [];
  const lines: TimedLine[] = [];
  let current: { words: TranscriptWord[]; startMs: number; endMs: number } | undefined;

  const flush = () => {
    if (!current) return;
    lines.push({
      startMs: current.startMs,
      endMs: current.endMs,
      text: current.words.map(word => word.word).join(" "),
      words: current.words,
    });
    current = undefined;
  };

  for (let index = 0; index < words.length; index++) {
    const item = words[index];
    const hitMaxWords = Boolean(current && current.words.length >= SPOKEN_PHRASE_MAX_WORDS);
    const leftover = hitMaxWords ? remainingWordsUntilSpokenBreak(words, index, current!.endMs) : 0;
    if (
      current &&
      (item.startMs - current.endMs > SPOKEN_PHRASE_GAP_MS || (hitMaxWords && leftover > SPOKEN_PHRASE_ORPHAN_WORDS))
    ) {
      flush();
    }
    if (!current) {
      current = { words: [item], startMs: item.startMs, endMs: item.endMs };
    } else {
      current.words.push(item);
      current.endMs = item.endMs;
    }
    if (endsSpokenPhrase(item.word)) flush();
  }
  flush();
  return lines;
}

export function spokenLinesFromMedia(media?: Media): { lines: TimedLine[]; untimed: string[] } {
  const words = spokenWordsFromMedia(media);
  if (words.length) {
    return { lines: spokenTimelineLines(words), untimed: [] };
  }
  const text = transcriptFromMedia(media);
  return { lines: [], untimed: text ? transcriptLines(text) : [] };
}

export function activeTimedLineIndex(lines: TimedLine[], currentMs: number): number {
  if (!lines.length) return -1;
  const exact = lines.findIndex(line => line.startMs <= currentMs && currentMs < line.endMs);
  if (exact >= 0) return exact;
  let lastStarted = -1;
  for (let index = 0; index < lines.length; index++) {
    if (lines[index].startMs <= currentMs) lastStarted = index;
  }
  return lastStarted;
}

export function seekSecondsForTimedLine(line: Pick<TimedLine, "startMs">): number {
  return line.startMs / 1000;
}

export function scrollTopToCenterElement(
  containerScrollTop: number,
  containerTop: number,
  containerHeight: number,
  elementTop: number,
  elementHeight: number
): number {
  return containerScrollTop + (elementTop + elementHeight / 2) - (containerTop + containerHeight / 2);
}

export function clampScrollTop(scrollTop: number, scrollHeight: number, clientHeight: number): number {
  return Math.max(0, Math.min(scrollTop, Math.max(0, scrollHeight - clientHeight)));
}

export function shouldPollTranscriptStatus(media?: Media): boolean {
  if (!media) return false;
  if (media.transcriptStatus === "AVAILABLE" || media.transcriptStatus === "FAILED") return false;
  if (media.transcriptStatus === "WAITING") return true;
  if (!isVideoMedia(media)) return false;
  return !transcriptFromMedia(media);
}

export function shouldPollEditedSpokenTranscripts(editedMedia?: Media[]): boolean {
  return (editedMedia || []).some(media => shouldPollTranscriptStatus(media));
}

function transcriptPollMediaSignature(media?: Media | null) {
  if (!media) return null;
  return {
    id: media.id,
    transcriptStatus: media.transcriptStatus ?? null,
    transcripts: (media.transcripts || []).map(transcript => ({
      content: transcript.content ?? "",
      timeline: transcript.timeline ?? [],
    })),
  };
}

export function hasTranscriptPollChange(
  current?: Pick<VideoProject, "editedMedia"> | null,
  latest?: Pick<VideoProject, "editedMedia"> | null
): boolean {
  return (
    JSON.stringify((current?.editedMedia || []).map(transcriptPollMediaSignature)) !==
    JSON.stringify((latest?.editedMedia || []).map(transcriptPollMediaSignature))
  );
}

function hasOnScreenCaptions(media?: Media): boolean {
  return Boolean(
    srtCuesFromMedia(media).length || media?.caption?.content || media?.caption?.uri || media?.caption?.file
  );
}

export function shouldShowOnScreenStack(media?: Media, source?: QaStudioSource): boolean {
  return source === "EDITED" && hasOnScreenCaptions(media);
}

export function shouldShowSpokenStack(media?: Media): boolean {
  if (!media) return false;
  if (media.transcriptStatus || shouldPollTranscriptStatus(media)) return true;
  const spoken = spokenLinesFromMedia(media);
  return spoken.lines.length > 0 || spoken.untimed.length > 0;
}

export function shouldShowTranscriptRail(media?: Media, source?: QaStudioSource): boolean {
  return shouldShowOnScreenStack(media, source) || shouldShowSpokenStack(media);
}

export function spokenTextFromMedia(media?: Media): string {
  const spoken = spokenLinesFromMedia(media);
  if (spoken.lines.length) return spoken.lines.map(line => line.text).join(" ");
  return spoken.untimed.join(" ");
}

export function shouldShowTranscriptComparison(media?: Media, source?: QaStudioSource): boolean {
  if (!shouldShowOnScreenStack(media, source)) return false;
  if (!srtCuesFromMedia(media).length) return false;
  if (media?.transcriptStatus === "FAILED") return false;
  const spoken = spokenLinesFromMedia(media);
  if (shouldPollTranscriptStatus(media) && !spoken.lines.length && !spoken.untimed.length) return false;
  return spoken.lines.length > 0 || spoken.untimed.length > 0;
}

export function tokenizeForDiff(text: string): DiffToken[] {
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(display => ({
      display,
      key: display.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ""),
    }))
    .filter(token => token.key);
}

export function tokenizeSpokenWords(words: TranscriptWord[]): DiffToken[] {
  return words.flatMap(word =>
    tokenizeForDiff(word.word).map(token => ({
      ...token,
      startMs: word.startMs,
      endMs: word.endMs,
    }))
  );
}

type DiffOp<T> = { type: "equal"; a: T; b: T } | { type: "delete"; a: T } | { type: "insert"; b: T };

function lcsDiff<T>(a: T[], b: T[], equal: (left: T, right: T) => boolean): DiffOp<T>[] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i][j] = equal(a[i - 1], b[j - 1]) ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const ops: DiffOp<T>[] = [];
  let i = n;
  let j = m;
  while (i > 0 && j > 0) {
    if (equal(a[i - 1], b[j - 1])) {
      ops.push({ type: "equal", a: a[i - 1], b: b[j - 1] });
      i -= 1;
      j -= 1;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      ops.push({ type: "delete", a: a[i - 1] });
      i -= 1;
    } else {
      ops.push({ type: "insert", b: b[j - 1] });
      j -= 1;
    }
  }
  while (i > 0) {
    ops.push({ type: "delete", a: a[i - 1] });
    i -= 1;
  }
  while (j > 0) {
    ops.push({ type: "insert", b: b[j - 1] });
    j -= 1;
  }
  ops.reverse();
  return ops;
}

function diffPartFromToken(token: DiffToken, kind: DiffKind): DiffPart {
  return { text: token.display, kind, startMs: token.startMs, endMs: token.endMs };
}

export function wordDiffParts(
  onScreen: string,
  spoken: string,
  spokenWords?: TranscriptWord[]
): { left: DiffPart[]; right: DiffPart[] } {
  const a = tokenizeForDiff(onScreen);
  const b = spokenWords?.length ? tokenizeSpokenWords(spokenWords) : tokenizeForDiff(spoken);
  const left: DiffPart[] = [];
  const right: DiffPart[] = [];
  for (const op of lcsDiff(a, b, (leftToken, rightToken) => leftToken.key === rightToken.key)) {
    if (op.type === "equal") {
      left.push(diffPartFromToken(op.a, "equal"));
      right.push(diffPartFromToken(op.b, "equal"));
    } else if (op.type === "delete") {
      left.push(diffPartFromToken(op.a, "remove"));
    } else {
      right.push(diffPartFromToken(op.b, "add"));
    }
  }
  return { left, right };
}

function comparisonRow(input: {
  startMs?: number;
  endMs?: number;
  onScreenText?: string;
  spokenText?: string;
  spokenWords?: TranscriptWord[];
}): TranscriptComparisonRow {
  const spokenText = input.spokenWords?.length
    ? input.spokenWords.map(word => word.word).join(" ")
    : input.spokenText?.trim()
    ? input.spokenText
    : undefined;
  const onScreenText = input.onScreenText?.trim() ? input.onScreenText : undefined;
  const times = { startMs: input.startMs, endMs: input.endMs };
  if (onScreenText && spokenText) {
    const { left, right } = wordDiffParts(onScreenText, spokenText, input.spokenWords);
    return { ...times, onScreenText, spokenText, onScreenParts: left, spokenParts: right };
  }
  if (onScreenText) {
    return {
      ...times,
      onScreenText,
      spokenText,
      onScreenParts: tokenizeForDiff(onScreenText).map(token => diffPartFromToken(token, "remove")),
      spokenParts: [],
    };
  }
  return {
    ...times,
    spokenText,
    onScreenParts: [],
    spokenParts: (input.spokenWords?.length
      ? tokenizeSpokenWords(input.spokenWords)
      : tokenizeForDiff(spokenText || "")
    ).map(token => diffPartFromToken(token, "add")),
  };
}

function overlapMs(leftStart: number, leftEnd: number, rightStart: number, rightEnd: number): number {
  return Math.max(0, Math.min(leftEnd, rightEnd) - Math.max(leftStart, rightStart));
}

function assignWordToCueIndex(cues: TimedLine[], word: TranscriptWord): number {
  const overlapping = cues
    .map((cue, index) => ({
      index,
      overlap: overlapMs(cue.startMs, cue.endMs, word.startMs, word.endMs),
      containsMidpoint: cue.startMs <= (word.startMs + word.endMs) / 2 && (word.startMs + word.endMs) / 2 < cue.endMs,
    }))
    .filter(item => item.overlap > 0);
  if (!overlapping.length) return -1;
  return (
    overlapping.find(item => item.containsMidpoint)?.index ??
    overlapping.sort((left, right) => right.overlap - left.overlap)[0].index
  );
}

export function alignSrtWithSpokenWords(cues: TimedLine[], words: TranscriptWord[]): TranscriptComparisonRow[] {
  if (!cues.length) return [];
  const cueWords: TranscriptWord[][] = cues.map(() => []);
  const leftover: TranscriptWord[] = [];
  for (const word of words) {
    const index = assignWordToCueIndex(cues, word);
    if (index >= 0) cueWords[index].push(word);
    else leftover.push(word);
  }

  const gaps: TranscriptWord[][] = Array.from({ length: cues.length + 1 }, () => []);
  for (const word of leftover) {
    const gapIndex = cues.findIndex(cue => word.startMs < cue.startMs);
    gaps[gapIndex < 0 ? cues.length : gapIndex].push(word);
  }

  const rows: TranscriptComparisonRow[] = [];
  const pushSpokenOnly = (group: TranscriptWord[]) => {
    if (!group.length) return;
    rows.push(
      comparisonRow({
        startMs: group[0].startMs,
        endMs: group[group.length - 1].endMs,
        spokenWords: group,
      })
    );
  };

  for (let index = 0; index < cues.length; index++) {
    pushSpokenOnly(gaps[index]);
    rows.push(
      comparisonRow({
        startMs: cues[index].startMs,
        endMs: cues[index].endMs,
        onScreenText: cues[index].text,
        spokenWords: cueWords[index].length ? cueWords[index] : undefined,
      })
    );
  }
  pushSpokenOnly(gaps[cues.length]);
  return rows;
}

export function alignSrtWithUntimedSpoken(cues: TimedLine[], spokenText: string): TranscriptComparisonRow[] {
  if (!cues.length) return [];
  type IndexedToken = DiffToken & { cueIndex: number };
  const flatCue: IndexedToken[] = cues.flatMap((cue, cueIndex) =>
    tokenizeForDiff(cue.text).map(token => ({ ...token, cueIndex }))
  );
  const spokenTokens: IndexedToken[] = tokenizeForDiff(spokenText).map(token => ({ ...token, cueIndex: -1 }));
  const spokenByCue: string[][] = cues.map(() => []);
  const leading: string[] = [];
  const trailing: string[] = [];
  let currentCue = -1;
  let seenCueTokens = 0;

  for (const op of lcsDiff(flatCue, spokenTokens, (leftToken, rightToken) => leftToken.key === rightToken.key)) {
    if (op.type === "insert") {
      if (seenCueTokens === 0) leading.push(op.b.display);
      else if (seenCueTokens >= flatCue.length) trailing.push(op.b.display);
      else spokenByCue[currentCue]?.push(op.b.display);
    } else {
      currentCue = op.a.cueIndex;
      seenCueTokens += 1;
      if (op.type === "equal") spokenByCue[currentCue].push(op.b.display);
    }
  }

  const rows: TranscriptComparisonRow[] = [];
  if (leading.length) rows.push(comparisonRow({ spokenText: leading.join(" ") }));
  for (let index = 0; index < cues.length; index++) {
    rows.push(
      comparisonRow({
        startMs: cues[index].startMs,
        endMs: cues[index].endMs,
        onScreenText: cues[index].text,
        spokenText: spokenByCue[index].length ? spokenByCue[index].join(" ") : undefined,
      })
    );
  }
  if (trailing.length) rows.push(comparisonRow({ spokenText: trailing.join(" ") }));
  return rows;
}

export function transcriptComparisonFromMedia(media?: Media): TranscriptComparisonRow[] {
  const cues = srtCuesFromMedia(media);
  if (!cues.length) return [];
  const words = spokenWordsFromMedia(media);
  if (words.length) return alignSrtWithSpokenWords(cues, words);
  const spokenText = spokenTextFromMedia(media);
  if (!spokenText.trim()) return [];
  return alignSrtWithUntimedSpoken(cues, spokenText);
}

export function comparisonDifferenceCount(rows: TranscriptComparisonRow[]): number {
  return rows.reduce((sum, row) => {
    return (
      sum +
      row.onScreenParts.filter(part => part.kind === "remove").length +
      row.spokenParts.filter(part => part.kind === "add").length
    );
  }, 0);
}

export function activeComparisonRowIndex(rows: TranscriptComparisonRow[], currentMs: number): number {
  const timed = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.startMs != null && row.endMs != null);
  if (!timed.length) return -1;
  const inner = activeTimedLineIndex(
    timed.map(({ row }) => ({ startMs: row.startMs!, endMs: row.endMs!, text: "" })),
    currentMs
  );
  return inner < 0 ? -1 : timed[inner].index;
}

export function comparisonRowAsTimedLine(row: TranscriptComparisonRow): TimedLine | undefined {
  if (row.startMs == null) return undefined;
  return {
    startMs: row.startMs,
    endMs: row.endMs ?? row.startMs,
    text: row.onScreenText || row.spokenText || "",
  };
}

export function activeTimedPartIndex(parts: Array<Pick<DiffPart, "startMs" | "endMs">>, currentMs: number): number {
  return parts.findIndex(
    part => part.startMs != null && part.endMs != null && part.startMs <= currentMs && currentMs < part.endMs
  );
}

export function timedPartAsTimedLine(part: Pick<DiffPart, "text" | "startMs" | "endMs">): TimedLine | undefined {
  if (part.startMs == null) return undefined;
  return { startMs: part.startMs, endMs: part.endMs ?? part.startMs, text: part.text };
}

export function wordingDifferenceCount(a: string, b: string): number {
  const remaining = new Map<string, number>();
  for (const token of tokenizeForDiff(a)) remaining.set(token.key, (remaining.get(token.key) || 0) + 1);
  let unmatched = 0;
  for (const token of tokenizeForDiff(b)) {
    const count = remaining.get(token.key) || 0;
    if (count > 0) remaining.set(token.key, count - 1);
    else unmatched += 1;
  }
  for (const count of remaining.values()) unmatched += count;
  return unmatched;
}

export function transcriptLines(transcript: string): string[] {
  const blocks = transcript
    .split(/\n+/)
    .map(line => line.trim())
    .filter(Boolean);
  if (blocks.length > 1) return blocks;
  return transcript
    .split(/(?<=[.?!])\s+/)
    .map(line => line.trim())
    .filter(Boolean);
}

export function isWatchComplete(playedRatio: number, skippedWatch: boolean): boolean {
  return skippedWatch || playedRatio >= QA_WATCH_COMPLETE_RATIO;
}

export function clampPlayheadSeconds(seconds: number, duration: number): number {
  if (!Number.isFinite(seconds) || !Number.isFinite(duration) || duration <= 0) return 0;
  return Math.min(Math.max(seconds, 0), Math.max(duration - 0.05, 0));
}

export function lastRejectedAudit(audits?: VideoProject.QaAudit[] | null): VideoProject.QaAudit | undefined {
  return audits?.find(audit => audit.result === "REJECTED");
}

export function hasReadableNotes(playerNotes?: VideoProject.ClientNote, coachNotes?: VideoProject.ClientNote): boolean {
  return Boolean(playerNotes?.notes?.trim() || coachNotes?.notes?.trim());
}

export function categoryFromShortcut(key: string): VideoProject.QaIssueCategory | undefined {
  return VideoProject.QaIssueCategories.find(category => VideoProject.QaIssueCategoryMeta[category].shortcut === key);
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}
